/**
 * Orchestrator des Umsatzsteuer-Rechenkerns (R12): computeVatReturn(input) → VatEngineResult.
 *
 * Reihenfolge: Eingaben sortieren → Kontext → Regeln R4–R9 + § 17-Adjustments → Eigentümerfenster (R8)
 * → Filter auf die Monate des Zeitraums → Kennzahlen (R11), NEGATIVE_KZ, § 19-Monitor → Prüfhinweise
 * zusammenführen/sortieren → Zeilen sortieren → Vorsteuer-Schlüssel je Immobilie → inputHash.
 * Kein Zugriff auf die Systemuhr: „jetzt" ist ausschließlich input.now.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {computeInputHash} from './hash';
import {mergeIssues, sortIssues} from './issues';
import {buildKennzahlenIssues, buildMonitorIssues, computeKennzahlen, computeMonitor19} from './kennzahlen';
import {monthKeysOfPeriod} from './month';
import {buildRuleContext, compareByID, sortEngineInput} from './rule-context';
import {buildIstLines} from './rules/ist';
import {buildMindestIstLines} from './rules/mindest-ist';
import {buildAdjustmentLines, buildMinderungLines} from './rules/minderung';
import {applyOwnershipToLines, buildPendingChangeIssues} from './rules/ownership';
import {buildSollLines} from './rules/soll';
import {buildVorsteuerLines, summarizeInputTaxByProperty} from './rules/vorsteuer';
import {ENGINE_VERSION, mergeRuleOutputs, VatEngineInput, VatEngineResult, VatIssue} from './types';

export function computeVatReturn(rawInput: VatEngineInput): VatEngineResult {
    const input = sortEngineInput(rawInput);
    const ctx = buildRuleContext(input);

    const rules = mergeRuleOutputs(
        buildSollLines(ctx, input.bookings),
        buildMindestIstLines(ctx, input.bookings),
        buildIstLines(ctx, input.payments, input.bookings),
        buildMinderungLines(ctx, input.bookings),
        buildVorsteuerLines(ctx, input.expenses),
        buildAdjustmentLines(ctx),
    );
    const owned = applyOwnershipToLines(ctx, rules.lines);

    const monthKeys = monthKeysOfPeriod(input.range.year, input.range.periodKey);
    const inRange = new Set(monthKeys);
    const lines = owned.lines.filter(line => inRange.has(line.monthKey)).sort(compareByID);
    const lineIDs = new Set(lines.map(line => line.id));

    // Prüfhinweise ohne Zeilenbezug bleiben global; zeilengebundene entfallen, wenn keine ihrer Zeilen im Zeitraum liegt.
    const scopedIssues: VatIssue[] = [];
    for (const issue of [...rules.issues, ...owned.issues, ...buildPendingChangeIssues(ctx)]) {
        if (issue.lineIDs.length === 0) {
            scopedIssues.push(issue);
            continue;
        }
        const remaining = issue.lineIDs.filter(id => lineIDs.has(id));
        if (remaining.length > 0) {
            scopedIssues.push({...issue, lineIDs: remaining});
        }
    }

    const kennzahlen = computeKennzahlen(lines, input.range, input.profile);
    const statusAtStart = input.profileAt(input.range.start).status;
    const monitor19 = computeMonitor19(lines, statusAtStart, input.monitor19Input);
    const issues = sortIssues(mergeIssues([
        ...scopedIssues,
        ...buildKennzahlenIssues(kennzahlen),
        ...buildMonitorIssues(monitor19, statusAtStart, input.landlordID),
    ]));

    const propertyIDs = [...input.bookings, ...input.payments, ...input.expenses].map(entity => entity.propertyID);
    const inputTaxByProperty = statusAtStart === 'REGELBESTEUERUNG'
        ? summarizeInputTaxByProperty(ctx, propertyIDs, monthKeys[monthKeys.length - 1])
        : [];

    return {
        kennzahlen,
        lines,
        issues,
        inputTaxByProperty,
        monitor19,
        stats: {
            lineCount: lines.length,
            bookingCount: input.bookings.length,
            paymentCount: input.payments.length,
            expenseCount: input.expenses.length,
        },
        inputHash: computeInputHash(input),
        engineVersion: ENGINE_VERSION,
    };
}
